import React, { FC } from 'react'
import styled, { keyframes } from 'styled-components'

import {
  ButtonStyled,
  SubTitle,
  HeaderTitle,
  ButtonDemoAccount,
} from '../CssStyled'
import Link from '@ads/front-core/atoms/Link'
import { breakpoints } from '@ads/front-core/constants/breakpoints'
import InfinityObserver from 'components/Observer/InfinityObserver'
import HalfCircleIcon from './HalfCircle'
import CirclesIcon from './Circles'
import CurvyLine2Icon from './CurvyLine2'
import CurvyLine1Icon from './CurvyLine1'
import { ISection } from '../../interfaces'
import { Trans, lng } from '@ads/front-core/locales'
import { Box } from '@wirtualna-polska/n2cr-ds'
import { loginToDemoAccount } from '../utils'

const Hero: FC<ISection> = ({ setActiveMenu }) => (
  <InfinityObserver callback={() => setActiveMenu('hero')}>
    <Container>
      <HeaderWrapper>
        <HeaderTitle>
          <div>
            <Trans
              i18nKey="landing:title1"
              components={{ bold: <HeaderTitle bold /> }}
            />
          </div>
          <div>
            <Trans
              i18nKey="landing:title2"
              components={{ bold: <HeaderTitle bold /> }}
            />
          </div>
        </HeaderTitle>
      </HeaderWrapper>
      <Content>
        <ButtonsWrapper>
          <ButtonGetStarted to="/1login/">
            {lng('landing:startWith')}
          </ButtonGetStarted>
          <ButtonDemoAccount color="grey50" onClick={loginToDemoAccount}>
            {lng('landing:viewDemoVersion')}
          </ButtonDemoAccount>
        </ButtonsWrapper>
        <HalfCircleContainer>
          <HalfCircleWrapper>
            <HalfCircleIcon />
          </HalfCircleWrapper>
          <SubTitleStyled>
            <Trans
              i18nKey="landing:advertiseYourself"
              components={{ bold: <SubTitle bold /> }}
            />
          </SubTitleStyled>
        </HalfCircleContainer>
        <HalfCircleContainer>
          <HalfCircleWrapper>
            <HalfCircleIcon />
          </HalfCircleWrapper>
          <SubTitleStyled>
            <Trans
              i18nKey="landing:realizeCampaigns"
              components={{ bold: <SubTitle bold /> }}
            />
          </SubTitleStyled>
        </HalfCircleContainer>
        <HalfCircleContainer>
          <HalfCircleWrapper>
            <HalfCircleIcon />
          </HalfCircleWrapper>
          <SubTitleStyled>
            <Trans
              i18nKey="landing:sendMails"
              components={{ bold: <SubTitle bold /> }}
            />
          </SubTitleStyled>
        </HalfCircleContainer>
      </Content>

      <MonitorWrapper>
        <Monitor>
          <img
            src="/img/landing/hero/monitor.png"
            alt={lng('landing:monitorAlt')}
          />
          <CirclesWrapper>
            <CirclesIcon />
          </CirclesWrapper>
          <AnimationContainer>
            <Animation />
            <Box styleCss={{ display: 'none' }}>
              <img src="/img/landing/hero/01.png" />
              <img src="/img/landing/hero/02.png" />
              <img src="/img/landing/hero/03.png" />
              <img src="/img/landing/hero/04.png" />
              <img src="/img/landing/hero/05.png" />
              <img src="/img/landing/hero/06.png" />
              <img src="/img/landing/hero/07.png" />
              <img src="/img/landing/hero/08.png" />
              <img src="/img/landing/hero/09.png" />
              <img src="/img/landing/hero/10.png" />
            </Box>
          </AnimationContainer>
          <CurvyLine1IconWrapper>
            <CurvyLine1Icon />
          </CurvyLine1IconWrapper>
        </Monitor>
        <img src="/img/landing/hero/stand.png" />
      </MonitorWrapper>
      <PotentialLineWrapper>
        <CurvyLine2Icon />
      </PotentialLineWrapper>
    </Container>
  </InfinityObserver>
)

const Container = styled.div`
  display: grid;
  position: relative;
  grid-template-columns: 560px 1fr;

  @media (max-width: ${breakpoints.MOBILE}) {
    grid-template-columns: 1fr;
    margin-bottom: 45px;
  }
`

const HeaderWrapper = styled.div`
  grid-column: 1/3;
  margin: 44px 0 26px;
`

const Content = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
  margin-top: 44px;
  grid-column: 1/2;

  @media (max-width: ${breakpoints.MOBILE}) {
    margin-top: 14px;
    gap: 16px;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: ${breakpoints.MOBILE}) {
    flex-direction: column;
    align-items: center;
  }
`

const ButtonGetStarted = styled(Link)`
  ${ButtonStyled}

  @media (max-width: ${breakpoints.MOBILE}) {
    align-self: center;
  }
`

const HalfCircleContainer = styled.div`
  display: flex;
  align-items: center;
`

const HalfCircleWrapper = styled.div`
  flex: none;

  @media (max-width: ${breakpoints.MOBILE}) {
    display: none;
  }
`

const MonitorWrapper = styled.div`
  grid-column: 2 / 2;
  justify-self: flex-end;
  margin-left: 32px;

  img {
    max-width: 100%;
  }

  @media (max-width: ${breakpoints.MOBILE}) {
    grid-column: 1;
    grid-row: 1;
    justify-self: center;
    margin-top: 62px;
    margin-left: 0;
  }
`

const Monitor = styled.div`
  position: relative;
  display: flex;
`

const SubTitleStyled = styled(SubTitle)`
  margin-left: 24px;

  @media (max-width: ${breakpoints.MOBILE}) {
    margin-left: 0;

    &,
    span {
      font-size: 18px;
      line-height: 27px;
    }
  }
`

const CirclesWrapper = styled.div`
  position: absolute;
  right: -145px;
  top: -175px;
  z-index: -1;
`

const CurvyLine1IconWrapper = styled.div`
  position: absolute;
  top: -112px;
  left: 50%;
  transform: translate(-50%, 0);

  @media (max-width: ${breakpoints.MOBILE}) {
    display: none;
  }
`

const PotentialLineWrapper = styled.div`
  position: absolute;
  top: 582px;
  left: 0;
  right: 0;

  svg {
    width: 100%;
  }

  @media (max-width: ${breakpoints.MOBILE}) {
    display: none;
  }
`

const AnimationContainer = styled.div`
  position: absolute;
  padding: 16px;
  width: 100%;
  height: 100%;
  overflow: hidden;

  @media (max-width: 500px) {
    padding: 9px;
  }
`

const imageLoop = keyframes`
  0%, 4%, 100% {
    background-image: url('/img/landing/hero/01.png');
  }
  10%, 14% {
    background-image: url('/img/landing/hero/02.png');  
  }
  20%, 24% {
    background-image: url('/img/landing/hero/03.png');  
  }
  30%, 34% {
    background-image: url('/img/landing/hero/04.png');  
  }
  40%, 44% {
    background-image: url('/img/landing/hero/05.png'); 
  }
  50%, 54% {
    background-image: url('/img/landing/hero/06.png');  
  }
  60%, 64% {
    background-image: url('/img/landing/hero/07.png');  
  }
  70%, 74% {
    background-image: url('/img/landing/hero/08.png');  
  }
  80%, 84% {
    background-image: url('/img/landing/hero/09.png');  
  }
  90%, 94% {
    background-image: url('/img/landing/hero/10.png');  
  }
`

const Animation = styled.div`
  width: 100%;
  height: 100%;
  background-image: url('/img/landing/hero/01.png');
  background-repeat: no-repeat;
  background-size: cover;
  animation: ${imageLoop} 16s infinite;
  animation-delay: 1s;
`

export default Hero
